import type { MCPClientCheckContext, MCPClientCheckDefinition } from "../types.js";
import {
  errorMessage,
  failedResult,
  passedResult,
} from "./helpers.js";

function selectCompletionReference(ctx: Parameters<
  MCPClientCheckDefinition["run"]
>[0]) {
  if (ctx.availablePrompts[0]) {
    return {
      ref: {
        type: "ref/prompt" as const,
        name: ctx.availablePrompts[0],
      },
      argument: {
        name: "value",
        value: "par",
      },
    };
  }

  if (ctx.availableResourceTemplates[0]) {
    return {
      ref: {
        type: "ref/resource" as const,
        uri: ctx.availableResourceTemplates[0],
      },
      argument: {
        name: "id",
        value: "123",
      },
    };
  }

  return undefined;
}

function checkCapabilityConsistency(
  caps: Record<string, unknown>,
  ctx: MCPClientCheckContext,
  mismatches: string[],
) {
  const hasToolsCap = !!caps.tools;
  const hasTools = ctx.availableTools.length > 0;
  if (hasTools && !hasToolsCap) {
    mismatches.push(
      "Server returned tools but does not advertise capabilities.tools",
    );
  }

  const hasPromptsCap = !!caps.prompts;
  const hasPrompts = ctx.availablePrompts.length > 0;
  if (hasPrompts && !hasPromptsCap) {
    mismatches.push(
      "Server returned prompts but does not advertise capabilities.prompts",
    );
  }

  const hasResourcesCap = !!caps.resources;
  const hasResources =
    ctx.availableResources.length > 0 ||
    ctx.availableResourceTemplates.length > 0;
  if (hasResources && !hasResourcesCap) {
    mismatches.push(
      "Server returned resources but does not advertise capabilities.resources",
    );
  }
}

export const CORE_CHECKS: MCPClientCheckDefinition[] = [
  {
    id: "server-initialize",
    category: "core",
    title: "Server Initialize",
    description: "Server responds to initialize and reports capabilities.",
    async run(ctx) {
      const startedAt = Date.now();
      const info = ctx.initializationInfo;
      if (!info) {
        return failedResult(
          this,
          Date.now() - startedAt,
          "Initialization info is unavailable after connecting to the server",
        );
      }

      return passedResult(this, Date.now() - startedAt, {
        protocolVersion: info.protocolVersion,
        transport: info.transport,
        serverCapabilities: info.serverCapabilities as Record<string, unknown>,
        serverVersion: info.serverVersion as Record<string, unknown>,
      });
    },
  },
  {
    id: "ping",
    category: "core",
    title: "Ping",
    description: "Server responds to ping requests.",
    async run(ctx) {
      const startedAt = Date.now();
      try {
        const result = await ctx.manager.pingServer(ctx.serverId);
        return passedResult(this, Date.now() - startedAt, {
          result: result as Record<string, unknown>,
        });
      } catch (error) {
        return failedResult(
          this,
          Date.now() - startedAt,
          errorMessage(error),
          undefined,
          error,
        );
      }
    },
  },
  {
    id: "logging-set-level",
    category: "core",
    title: "Logging Set Level",
    description: "Server accepts logging/setLevel requests.",
    async run(ctx) {
      const startedAt = Date.now();
      if (!ctx.initializationInfo?.serverCapabilities?.logging) {
        return {
          ...this,
          status: "skipped" as const,
          skipReason: "not-applicable" as const,
          durationMs: 0,
          error: {
            message: "Server does not advertise the optional logging capability",
          },
        };
      }

      try {
        // The managed client resolves `setLoggingLevel` to `void` (upstream
        // returns an `EmptyResult` the manager discards). A clean resolve —
        // no thrown JSON-RPC error — is the pass condition; there is no
        // result body to inspect.
        await ctx.client.setLoggingLevel("info");

        return passedResult(this, Date.now() - startedAt, {
          level: "info",
        });
      } catch (error) {
        return failedResult(
          this,
          Date.now() - startedAt,
          errorMessage(error),
          undefined,
          error,
        );
      }
    },
  },
  {
    id: "completion-complete",
    category: "core",
    title: "Completion Complete",
    description: "Server responds to completion/complete requests.",
    async run(ctx) {
      const startedAt = Date.now();
      if (!ctx.initializationInfo?.serverCapabilities?.completions) {
        return {
          ...this,
          status: "skipped" as const,
          skipReason: "not-applicable" as const,
          durationMs: 0,
          error: {
            message:
              "Server does not advertise the optional completions capability",
          },
        };
      }

      const completionReference = selectCompletionReference(ctx);
      if (!completionReference) {
        return {
          ...this,
          status: "skipped" as const,
          // Completions IS advertised, so the requirement applies here — we
          // simply found no subject to exercise it against.
          skipReason: "could-not-run" as const,
          durationMs: 0,
          error: {
            message:
              "Server does not expose a prompt or resource template suitable for completion testing",
          },
        };
      }

      try {
        const result = await ctx.client.complete(completionReference);
        if (!Array.isArray(result.completion.values)) {
          return failedResult(
            this,
            Date.now() - startedAt,
            "completion/complete did not return a values array",
            {
              result: result as Record<string, unknown>,
            },
          );
        }

        return passedResult(this, Date.now() - startedAt, {
          completion: result.completion as Record<string, unknown>,
        });
      } catch (error) {
        return failedResult(
          this,
          Date.now() - startedAt,
          errorMessage(error),
          undefined,
          error,
        );
      }
    },
  },
  {
    id: "modern-client-handshake",
    category: "core",
    title: "Modern Client Handshake",
    description:
      "The official client negotiates the modern revision and receives the server identity and capabilities.",
    async run(ctx) {
      const startedAt = Date.now();
      const info = ctx.initializationInfo;
      if (!info) {
        return failedResult(
          this,
          Date.now() - startedAt,
          "Initialization info is unavailable after connecting to the server",
        );
      }

      // The modern era has no `initialize`; discovery is what the client
      // performed to get here, so this is the client-provable half of the
      // discovery requirement. The wire-level half (`server/discover`'s exact
      // result shape) is asserted by `modern-server-discover`.
      const problems: string[] = [];
      if (info.protocolVersion !== ctx.config.protocolVersion) {
        problems.push(
          `negotiated protocol version ${String(info.protocolVersion)} does not match the pinned ${String(ctx.config.protocolVersion)}`,
        );
      }
      if (!info.serverCapabilities) {
        problems.push("server capabilities are missing");
      }
      if (!info.serverVersion?.name) {
        problems.push("server identity (name) is missing");
      }

      if (problems.length > 0) {
        return failedResult(
          this,
          Date.now() - startedAt,
          `Modern handshake is incomplete: ${problems.join("; ")}`,
          {
            protocolVersion: info.protocolVersion,
            serverVersion: info.serverVersion as Record<string, unknown>,
          },
        );
      }

      return passedResult(this, Date.now() - startedAt, {
        protocolVersion: info.protocolVersion,
        transport: info.transport,
        serverCapabilities: info.serverCapabilities as Record<string, unknown>,
        serverVersion: info.serverVersion as Record<string, unknown>,
      });
    },
  },
  {
    id: "capabilities-consistent",
    category: "core",
    title: "Capabilities Consistent",
    description:
      "Advertised server capabilities match the features actually exposed.",
    async run(ctx) {
      const startedAt = Date.now();
      const caps = ctx.initializationInfo?.serverCapabilities;
      if (!caps) {
        return failedResult(
          this,
          Date.now() - startedAt,
          "Server capabilities are unavailable after initialization",
        );
      }

      const mismatches: string[] = [];
      checkCapabilityConsistency(caps, ctx, mismatches);

      if (mismatches.length > 0) {
        return failedResult(
          this,
          Date.now() - startedAt,
          `Capability mismatches: ${mismatches.join("; ")}`,
          { mismatches },
        );
      }

      return passedResult(this, Date.now() - startedAt, {
        advertisedCapabilities: Object.keys(caps),
      });
    },
  },
];
